/* eslint-disable no-await-in-loop */
import React, { useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { Button } from '@vlabs/uikit/components/controls/button/Button';
import { Radio } from '@vlabs/uikit/components/controls/radio/Radio';
import type { WithDataTestid } from '@vlabs/uikit/types';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import st from './ClearLists.module.sass';
import {
  detachAllCardsFromList, detachAllFacesFromList, deleteAllCardsFromList, deleteAllFacesFromList,
} from './requests';
import { buildTestid } from '../../shared/lib/utils';

const displayName = 'ClearLists';
const testid = buildTestid(displayName);

type TClearAction = 'delete' | 'detach';

interface ClearListsProps extends WithDataTestid {
  listIds: string[];
  onCancel: VoidFunction;
  onClearLists?: VoidFunction;
}

export function ClearLists({
  listIds,
  onCancel,
  onClearLists = undefined,
  'data-testid': dataTestId = undefined,
}: ClearListsProps): React.JSX.Element {
  const { t } = useTranslation();
  const [clearAction, setClearAction] = useState<TClearAction>('delete');

  const clearListsMutation = useMutation({
    mutationFn: async ({ listIds: ids, clearAction: action }: { listIds: string[]; clearAction: TClearAction }) => {
      // eslint-disable-next-line no-restricted-syntax
      for (const id of ids) {
        if (action === 'delete') {
          await deleteAllCardsFromList(id);
          await deleteAllFacesFromList(id);
        } else {
          await detachAllCardsFromList(id);
          await detachAllFacesFromList(id);
        }
      }
    },
    onSuccess: () => {
      toast.success(t('features:clear_lists.toast.success'));
      onClearLists?.();
      onCancel();
    },
    onError: () => {
      toast.error(t('features:clear_lists.toast.error'));
    },
  });

  const handleSubmit = (): void => {
    clearListsMutation.mutate({
      listIds,
      clearAction,
    });
  };

  return (
    <div
      className={st.Root}
      {...testid(dataTestId)}
    >
      <header className={st.Header}>
        <h5>{t('features:clear_lists.confirmation.title')}</h5>
        <span className={st.Subtitle}>
          {t('features:clear_lists.confirmation.subtitle')}
        </span>
      </header>

      <div className={st.Content}>
        <Radio
          checked={clearAction === 'delete'}
          id="delete"
          label={t('features:clear_lists.confirmation.delete_content') as unknown as string}
          name="clearListAction"
          onChange={() => setClearAction('delete')}
          {...testid('Delete', dataTestId)}
        />

        <Radio
          checked={clearAction === 'detach'}
          id="detach"
          label={t('features:clear_lists.confirmation.detach_only') as unknown as string}
          name="clearListAction"
          onChange={() => setClearAction('detach')}
          {...testid('Detach', dataTestId)}
        />
      </div>

      <div className={st.Actions}>
        <Button
          className={st.Button}
          disabled={clearListsMutation.isPending}
          isLoading={clearListsMutation.isPending}
          onClick={handleSubmit}
          {...testid('SubmitButton', dataTestId)}
        >
          {t('features:clear_lists.btn.clear')}
        </Button>

        <Button
          className={st.Button}
          kind="secondary"
          onClick={onCancel}
          variant="outlined"
          {...testid('CancelButton', dataTestId)}
        >
          {t('features:clear_lists.btn.cancel')}
        </Button>
      </div>
    </div>
  );
}
